/** Body cam reviews: the supervisor review form, every review in one place, and the monthly report with training patterns. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { SignaturePad } from '../shared/affidavit.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI } from './common.tsx';
import { HookLinkCard } from './HookLink.tsx';

const now = new Date();
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function MonthPicker({ y, m, set }: { y: number; m: number; set: (y: number, m: number) => void }) {
  return (
    <div className="row gap-sm">
      <button type="button" className="btn sm ghost" aria-label="Previous month" onClick={() => (m === 1 ? set(y - 1, 12) : set(y, m - 1))}>
        ‹
      </button>
      <strong style={{ minWidth: 130, textAlign: 'center' }}>
        {MONTHS[m - 1]} {y}
      </strong>
      <button type="button" className="btn sm ghost" aria-label="Next month" onClick={() => (m === 12 ? set(y + 1, 1) : set(y, m + 1))}>
        ›
      </button>
    </div>
  );
}

export function BwcPage() {
  const { navigate } = useRouter();
  const [ym, setYm] = useState<[number, number]>([now.getFullYear(), now.getMonth() + 1]);
  const [tab, setTab] = useState<'reviews' | 'report'>(new URLSearchParams(location.search).get('tab') === 'report' ? 'report' : 'reviews');
  const [c, setC] = useState('');
  const r = useLoad(() => api.get<any>(`/api/bwc?year=${ym[0]}&month=${ym[1]}&compliant=${c}`), [ym[0], ym[1], c]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Personnel"
        title="Body cam reviews"
        sub="Supervisor reviews of body-worn camera video. The monthly report shows compliance and what to train on."
        actions={
          r.data?.canReview && (
            <button className="btn primary" onClick={() => navigate(at('/bwc/new'))}>
              New review
            </button>
          )
        }
      />
      <div className="row gap-sm wrap between">
        <div className="seg-ctl" role="tablist" aria-label="View">
          <button type="button" role="tab" aria-selected={tab === 'reviews'} className={tab === 'reviews' ? 'on' : ''} onClick={() => setTab('reviews')}>
            Reviews
          </button>
          <button type="button" role="tab" aria-selected={tab === 'report'} className={tab === 'report' ? 'on' : ''} onClick={() => setTab('report')}>
            Monthly report
          </button>
        </div>
        <MonthPicker y={ym[0]} m={ym[1]} set={(y, m) => setYm([y, m])} />
      </div>
      {tab === 'report' ? (
        <BwcReport y={ym[0]} m={ym[1]} />
      ) : (
        <>
          <div className="seg-ctl" role="tablist" aria-label="Compliance">
            {(
              [
                ['', 'All'],
                ['no', 'Not compliant'],
                ['yes', 'Compliant'],
              ] as const
            ).map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={c === k} className={c === k ? 'on' : ''} onClick={() => setC(k)}>
                {l}
              </button>
            ))}
          </div>
          {r.error ? (
            <ErrorBox error={r.error} />
          ) : !r.data ? (
            <Spinner />
          ) : !r.data.reviews.length ? (
            <EmptyState icon="monitor" title="No reviews this month" />
          ) : (
            <div className="card table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Officer</th>
                    <th>Video / case</th>
                    <th>Incident</th>
                    <th>Compliant</th>
                    <th>Action</th>
                    <th>Reviewed by</th>
                  </tr>
                </thead>
                <tbody>
                  {r.data.reviews.map((x: any) => (
                    <tr key={x.id} className="clickable" onClick={() => navigate(at(`/bwc/${x.id}`))}>
                      <td>
                        <strong>{x.officer_name}</strong>
                        <div className="small muted">{x.assignment}</div>
                      </td>
                      <td className="mono">{x.video_ref}</td>
                      <td>
                        {day(x.incident_date)}
                        <div className="small muted">{x.incident_types.join(', ')}</div>
                      </td>
                      <td>{x.compliant ? <Badge tone="ok">Yes</Badge> : <Badge tone="bad">No — {x.reasons.join(', ')}</Badge>}</td>
                      <td className="small">{x.actions.join(', ')}</td>
                      <td>
                        {x.supervisor_name}
                        <div className="small muted">
                          {day(x.reviewed_on)}
                          {x.source === 'jotform' ? ' · Jotform' : ''}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {r.data?.seesAll && <HookLinkCard title="Connect the BWC Supervisor Review Jotform" settings="/api/bwc/settings" rotate="/api/bwc/settings/link" what="BWC Supervisor Review" detail="Reviews supervisors still send through the Jotform then land here too, with the same fields." />}
        </>
      )}
    </div>
  );
}

function Bars({ rows, total }: { rows: { label: string; n: number }[]; total: number }) {
  if (!rows.length) return <p className="muted small">None.</p>;
  const max = Math.max(...rows.map((r) => r.n), 1);
  return (
    <ul className="bwc-bars">
      {rows.map((r) => (
        <li key={r.label}>
          <span className="bwc-l">{r.label}</span>
          <span className="bwc-b">
            <span style={{ width: `${(r.n / max) * 100}%` }} />
          </span>
          <span className="bwc-n">
            {r.n}
            {total ? <span className="muted"> ({Math.round((r.n / total) * 100)}%)</span> : null}
          </span>
        </li>
      ))}
    </ul>
  );
}

function BwcReport({ y, m }: { y: number; m: number }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/bwc/report?year=${y}&month=${m}`), [y, m]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const d = r.data;
  if (!d.total) return <EmptyState icon="chart" title={`No reviews for ${d.monthName} ${d.year}`} />;
  return (
    <div className="stack">
      <div className="ops-kpis">
        <KPI n={d.total} label="Reviews" />
        <KPI n={d.rate == null ? '—' : `${d.rate}%`} label="Compliant" tone={d.rate != null && d.rate < 90 ? 'warn' : 'ok'} />
        <KPI n={d.noncompliant} label="Not compliant" tone={d.noncompliant ? 'bad' : undefined} />
        <KPI n={d.iaReferrals.length} label="Referred to IA" tone={d.iaReferrals.length ? 'bad' : undefined} />
        <KPI n={d.fourVideosNo} label="Under 4 videos" tone={d.fourVideosNo ? 'warn' : undefined} />
      </div>
      <Card title="Training patterns">
        {d.patterns.length ? (
          <ul className="bwc-pat">
            {d.patterns.map((p: any, i: number) => (
              <li key={i}>
                <Badge tone={p.kind === 'agency' ? 'warn' : 'info'}>{p.kind === 'agency' ? 'Agency-wide' : 'Officer'}</Badge> {p.text}{' '}
                <button type="button" className="btn link small" onClick={() => navigate(at(`/training/classes?new=${encodeURIComponent(p.topic)}`))}>
                  Schedule training ›
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">Nothing repeating this month — no reason showed up across three or more officers, and no officer has two non-compliant reviews in three months.</p>
        )}
      </Card>
      <div className="grid-2-cards">
        <Card title="Why not compliant">
          <Bars rows={d.reasons} total={d.noncompliant} />
        </Card>
        <Card title="Supervisor actions">
          <Bars rows={d.actions} total={d.total} />
        </Card>
        <Card title="Incident types reviewed">
          <Bars rows={d.incidentTypes} total={d.total} />
        </Card>
        <Card title="By assignment">
          <table className="table compact">
            <thead>
              <tr>
                <th>Assignment</th>
                <th>Reviews</th>
                <th>Not compliant</th>
              </tr>
            </thead>
            <tbody>
              {d.byAssignment.map((x: any) => (
                <tr key={x.label}>
                  <td>{x.label}</td>
                  <td>{x.reviews}</td>
                  <td>{x.noncompliant}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <div className="grid-2-cards">
        <Card title="By officer">
          <table className="table compact">
            <thead>
              <tr>
                <th>Officer</th>
                <th>Reviews</th>
                <th>Not compliant</th>
                <th>Reasons</th>
              </tr>
            </thead>
            <tbody>
              {d.byOfficer.map((o: any) => (
                <tr key={o.name}>
                  <td>{o.name}</td>
                  <td>{o.reviews}</td>
                  <td>{o.noncompliant ? <strong>{o.noncompliant}</strong> : 0}</td>
                  <td className="small">{Object.entries(o.reasons).map(([k, v]) => `${k} ×${v}`).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card title="Reviews by supervisor">
          <table className="table compact">
            <tbody>
              {d.bySupervisor.map((s: any) => (
                <tr key={s.name}>
                  <td>{s.name}</td>
                  <td>{s.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
      <div className="no-print">
        <button className="btn" onClick={() => window.print()}>
          Print the report
        </button>
      </div>
    </div>
  );
}

export function NewBwc() {
  const { navigate } = useRouter();
  const meta = useLoad(() => api.get<any>('/api/bwc/meta'));
  const a = useAction();
  const today = new Date().toLocaleDateString('en-CA');
  const [f, setF] = useState<Record<string, any>>({ month: now.getMonth() + 1, year: now.getFullYear(), reviewedOn: today, signedOn: today, incidentTypes: [], reasons: [], actions: [], compliant: null, fourVideos: null });
  if (!meta.data) return meta.error ? <ErrorBox error={meta.error} /> : <Spinner />;
  const m = meta.data;
  const set = (k: string, v: unknown) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k: string, v: string) => set(k, (f[k] as string[]).includes(v) ? (f[k] as string[]).filter((x) => x !== v) : [...f[k], v]);
  const checks = (k: string, opts: readonly string[]) => (
    <div className="bwc-checks">
      {opts.map((o) => (
        <label key={o} className="check">
          <input type="checkbox" checked={(f[k] as string[]).includes(o)} onChange={() => toggle(k, o)} /> {o}
        </label>
      ))}
    </div>
  );
  const yesNo = (k: string, label: string) => (
    <fieldset className="bwc-yn">
      <legend>{label}</legend>
      {[
        [true, 'Yes'],
        [false, 'No'],
      ].map(([v, l]) => (
        <label key={String(v)} className="check">
          <input type="radio" name={k} checked={f[k] === v} onChange={() => set(k, v)} /> {l as string}
        </label>
      ))}
    </fieldset>
  );
  const officer = m.officers.find((o: any) => o.id === f.officerId);
  return (
    <div className="ops stack">
      <PageHeader crumb="Body cam reviews" title="BWC supervisor review" sub="The same questions as the review form. Everything here goes into the monthly report." />
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => navigate(at(`/bwc/${(await api.post<{ id: string }>('/api/bwc', f)).id}`), true));
        }}
      >
        <Card title="Supervisor">
          <div className="grid-3">
            <label>
              Supervisor name
              <input value={f.supervisorName ?? m.me.name} onChange={(e) => set('supervisorName', e.target.value)} />
            </label>
            <label>
              Rank / assignment
              <select required value={f.supervisorRank ?? ''} onChange={(e) => set('supervisorRank', e.target.value)}>
                <option value="">—</option>
                {m.ranks.map((r: string) => (
                  <option key={r}>{r}</option>
                ))}
              </select>
            </label>
            <label>
              Month
              <select value={f.month} onChange={(e) => set('month', Number(e.target.value))}>
                {m.months.map((x: string, i: number) => (
                  <option key={x} value={i + 1}>
                    {x}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Year
              <input type="number" value={f.year} onChange={(e) => set('year', Number(e.target.value))} />
            </label>
          </div>
        </Card>
        <Card title="Officer">
          <div className="grid-3">
            <label>
              Officer
              <select required value={f.officerId ?? ''} onChange={(e) => set('officerId', e.target.value || null)}>
                <option value="">—</option>
                {m.officers.map((o: any) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                    {o.call_number ? ` (${o.call_number})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Badge #
              <input value={f.officerBadge ?? officer?.badge ?? ''} onChange={(e) => set('officerBadge', e.target.value)} />
            </label>
            <label>
              Assignment
              <select required value={f.assignment ?? ''} onChange={(e) => set('assignment', e.target.value)}>
                <option value="">—</option>
                {m.assignments.map((x: string) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid-3">
            {yesNo('fourVideos', 'Four videos reviewed?')}
            <label>
              Last docked date
              <input type="date" value={f.lastDocked ?? ''} onChange={(e) => set('lastDocked', e.target.value)} />
            </label>
          </div>
        </Card>
        <Card title="The video">
          <div className="grid-3">
            <label>
              Video / case #
              <input required value={f.videoRef ?? ''} onChange={(e) => set('videoRef', e.target.value)} />
            </label>
            <label>
              Incident date
              <input type="date" required value={f.incidentDate ?? ''} onChange={(e) => set('incidentDate', e.target.value)} />
            </label>
            <label>
              Incident time
              <input type="time" value={f.incidentTime ?? ''} onChange={(e) => set('incidentTime', e.target.value)} />
            </label>
            <label>
              Date reviewed
              <input type="date" required value={f.reviewedOn} onChange={(e) => set('reviewedOn', e.target.value)} />
            </label>
          </div>
          <div className="small strong">Incident type</div>
          {checks('incidentTypes', m.incidentTypes)}
        </Card>
        <Card title="Compliance">
          {yesNo('compliant', 'Was the officer compliant with the BWC policy?')}
          {f.compliant === false && (
            <>
              <div className="small strong">Why not</div>
              {checks('reasons', m.reasons)}
              <label>
                Explanation
                <textarea rows={3} required value={f.explanation ?? ''} onChange={(e) => set('explanation', e.target.value)} />
              </label>
            </>
          )}
          <div className="small strong">Supervisor action</div>
          {checks('actions', m.actions)}
          {f.actions.includes('Referred to Internal Affairs / Professional Standards') && <Notice tone="warn">Saving this sends the Internal Affairs referral alert.</Notice>}
          <label>
            Comments
            <textarea rows={3} value={f.comments ?? ''} onChange={(e) => set('comments', e.target.value)} />
          </label>
        </Card>
        <Card title="Signature">
          <SignaturePad onChange={(d) => set('signature', d)} />
          <label style={{ maxWidth: 220 }}>
            Date
            <input type="date" value={f.signedOn} onChange={(e) => set('signedOn', e.target.value)} />
          </label>
        </Card>
        <ErrorBox error={a.error} />
        <div>
          <button className="btn primary" disabled={a.busy}>
            Save the review
          </button>
        </div>
      </form>
    </div>
  );
}

export function BwcDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/bwc/${id}`), [id]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const x = r.data.review;
  const yn = (b: boolean | null) => (b == null ? '—' : b ? 'Yes' : 'No');
  return (
    <div className="ops stack">
      <PageHeader crumb="Body cam reviews" title={`${x.officer_name} · ${x.video_ref}`} sub={`${MONTHS[x.month - 1]} ${x.year} · reviewed by ${x.supervisor_name} on ${day(x.reviewed_on)}${x.source === 'jotform' ? ' (from Jotform)' : ''}`} actions={x.compliant ? <Badge tone="ok">Compliant</Badge> : <Badge tone="bad">Not compliant</Badge>} />
      {r.data.iaCase ? (
        <Notice tone="info">
          Internal affairs case <a href={at(`/ia/${r.data.iaCase.id}`)}>{r.data.iaCase.number}</a> was opened from this review.
        </Notice>
      ) : (
        r.data.canOpenIa && (
          <Notice tone="warn">
            Referred to IA.{' '}
            <a className="btn sm primary" href={at(`/ia/new?bwc=${id}`)}>
              Open IA case
            </a>
          </Notice>
        )
      )}
      <div className="grid-2-cards">
        <Card title="Review">
          <dl className="kv">
            <dt>Supervisor</dt>
            <dd>
              {x.supervisor_name}
              {x.supervisor_rank ? ` — ${x.supervisor_rank}` : ''}
            </dd>
            <dt>Officer</dt>
            <dd>
              {x.officer_name}
              {x.officer_badge ? ` · badge ${x.officer_badge}` : ''} · {x.assignment}
            </dd>
            <dt>Four videos?</dt>
            <dd>{yn(x.four_videos)}</dd>
            <dt>Last docked</dt>
            <dd>{x.last_docked ? day(x.last_docked) : '—'}</dd>
            <dt>Incident</dt>
            <dd>
              {day(x.incident_date)} {x.incident_time ?? ''} · {x.incident_types.join(', ')}
            </dd>
            <dt>Compliant</dt>
            <dd>{yn(x.compliant)}</dd>
            {!x.compliant && (
              <>
                <dt>Why not</dt>
                <dd>{x.reasons.join(', ')}</dd>
                <dt>Explanation</dt>
                <dd className="pre">{x.explanation}</dd>
              </>
            )}
            <dt>Supervisor action</dt>
            <dd>{x.actions.join(', ')}</dd>
            <dt>Comments</dt>
            <dd className="pre">{x.comments ?? '—'}</dd>
          </dl>
        </Card>
        <Card title="Signed">
          {x.signature ? <img src={x.signature} alt="Supervisor’s signature" className="sig-img" /> : <p className="muted">No signature (from Jotform).</p>}
          <p className="small muted">{x.signed_on ? day(x.signed_on) : ''}</p>
        </Card>
      </div>
    </div>
  );
}
